import { useTranslation } from 'react-i18next';
import { useAppConfig } from './turnstile.tsx';

/**
 * The demo warning.
 *
 * A deployment with no subscription billing configured is a demonstration, not
 * a product: accounts work completely for a few days and then stop. Someone
 * who puts their real customers and real quotes into one and finds that out on
 * day four was not warned badly — they were not warned at all. So the notice is
 * unconditional, on every screen, signed in or not, and it says the two things
 * that actually matter: this will stop working, and do not run a business on it.
 *
 * It is not dismissible. A banner that can be closed is one that will be, well
 * before the day it starts mattering.
 */

/** Signed-out pages: the deployment's demo status comes from public config. */
export function DemoNotice() {
  const config = useAppConfig();
  if (!config.data?.demoMode) return null;
  return <DemoBanner days={config.data.demoExpirationDays} />;
}

/**
 * The banner itself, in the one style used everywhere it appears.
 *
 * `daysLeft` is what a signed-in company has left on its own clock, which is
 * the more useful number once there is an account to lose; before that there is
 * only `days`, the length a new account is granted.
 */
export function DemoBanner({ days, daysLeft }: { days: number; daysLeft?: number }) {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className="border-b-2 border-[color:var(--color-warning)] bg-[color:var(--color-warning-soft)] px-4 py-2.5"
    >
      <div className="mx-auto flex max-w-6xl flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
        <span className="badge border-[color:var(--color-warning)] font-bold uppercase tracking-wide text-[color:var(--color-warning)]">
          {t('demo.badge')}
        </span>
        <span className="font-semibold text-[color:var(--color-warning)]">
          {daysLeft === undefined
            ? t('demo.headline', { count: days })
            : t('demo.headlineSignedIn', { count: daysLeft })}
        </span>
        <span className="text-[color:var(--color-muted)]">{t('demo.doNotUseInProduction')}</span>
      </div>
    </div>
  );
}
